import Image from '@/features/shared/Image';
import {
  MuiAccordion,
  MuiAccordionDetails,
  MuiAccordionSummary,
  MuiBox,
  MuiTypography,
} from '@/features/shared/material-ui';
import RichText, { CaptureClicksRichText } from '@/features/shared/RichText';
import { Theme } from '@/utils/constants';
import { ExpandMore as ExpandMoreIcon } from '@material-ui/icons';
import clsx from 'clsx';
import React, { memo, useMemo } from 'react';
import { SideContentProps } from './SideContent.interface';
import useStyles from './SideContent.styles';

const HorizontalDivider = ({ className }: any) => {
  return <div className={className} />;
};

const Social = ({
  images = [],
  className = '',
}: {
  images: (ImageType & { url: string })[];
  className: string;
}) => {
  return (
    <MuiBox display="flex" alignItems="flex-start" className={className}>
      {images &&
        images.map((image, id) => (
          // eslint-disable-next-line react/no-array-index-key
          <MuiBox key={id} p={1}>
            <a
              href={image?.url}
              target="_blank"
              aria-label="social link"
              rel="noopener noreferrer"
              className="gtm-social"
            >
              <Image image={image} />
            </a>
          </MuiBox>
        ))}
    </MuiBox>
  );
};

const GenAccordion = ({
  isCollapsed = true,
  title,
  content,
  className,
  theme = Theme.light,
}: AccordionType & { className: string } & { theme: ThemeType }) => {
  const rootAccordionClasses = clsx(className, 'gtm-accordion');
  return (
    <MuiAccordion elevation={0} defaultExpanded={!isCollapsed} className={rootAccordionClasses}>
      <MuiAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <RichText theme={theme} content={title} />
      </MuiAccordionSummary>
      <MuiAccordionDetails>
        <RichText theme={theme} content={content} />
      </MuiAccordionDetails>
    </MuiAccordion>
  );
};

const mapComponentList = ({ variant }: any) => {
  const ComponentList: { [key: string]: any } = {
    socialPlatforms: Social,
    rtc: ({ className = '', ...props }: any) => (
      <CaptureClicksRichText {...props} containerProps={{ className }} />
    ),
    accordion: GenAccordion,
  };

  return variant && ComponentList[variant];
};

const Resource = ({
  sectionTitle: title,
  content,
  theme,
  last,
}: PressCenterResourcesType & { theme: ThemeType }) => {
  const classes = useStyles({});
  const { sectionDivider, contentList = [] } = content || {};
  const displaySectionDivider = sectionDivider && contentList.length > 0 && !last;

  return (
    <div className={classes.resource}>
      {title && (
        <MuiTypography variant="h4" color="inherit" className={classes.heading}>
          {title}
        </MuiTypography>
      )}
      <div className={classes.content}>
        {contentList &&
          contentList.map((item: any, id) => {
            const Variant = mapComponentList(item);
            return (
              <Variant
                // eslint-disable-next-line react/no-array-index-key
                key={id}
                {...item}
                theme={theme}
                className={classes[item.variant as 'rtc' | 'accordion' | 'socialPlatforms']}
              />
            );
          })}
      </div>
      {displaySectionDivider && <HorizontalDivider className={classes.divider} />}
    </div>
  );
};

const SideContent = ({
  resources = [],
  contentButtons,
  theme = Theme.light,
}: Br.PropsWithContentButtons<SideContentProps>) => {
  const classes = useStyles({});
  const components = useMemo(
    () =>
      Array.isArray(resources) &&
      resources.map((resource, id) => (
        // eslint-disable-next-line react/no-array-index-key
        <Resource key={id} {...resource} last={id === resources.length - 1} theme={theme} />
      )),
    [resources, theme],
  );
  const rootClasses = clsx(classes.root, `${theme}-theme`);
  return (
    <div className={rootClasses}>
      {contentButtons?.resources}
      {components}
    </div>
  );
};

export default memo(SideContent);
